export interface DeleteKeyParts {
    themeName: string;
    categoryName: string;
    cssName: string;
}

/**
 * Normalize a display name for case-insensitive comparisons.
 */
export const normalizeName = (name: string): string => (name || '').normalize('NFC').trim().toLowerCase();

/**
 * Build the composite key that tracks deletion/restoration targets.
 */
export const makeDeleteKey = (theme: string, category: string, css: string): string => {
    return `${theme}__${category || ''}__${css}`;
};

/**
 * Parse a composite key that was produced by `makeDeleteKey`.
 * Falls back to legacy underscore-based keys for backward compatibility.
 */
export const parseDeleteKey = (key: string): DeleteKeyParts => {
    const parts = key.split('__');
    if (parts.length === 3) {
        const [themeName, categoryName, cssName] = parts;
        return { themeName, categoryName, cssName };
    }

    const legacy = key.split('_');
    if (legacy.length >= 2) {
        const [themeName, cssName] = legacy as [string, string];
        return { themeName, categoryName: '', cssName };
    }

    return { themeName: '', categoryName: '', cssName: '' };
};

/**
 * Determine if a theme should be considered the default entry.
 */
export const isDefaultTheme = (theme?: { themeId?: string; themeName?: string } | null): boolean => {
    if (!theme) return false;
    const normalized = (theme.themeName || '').normalize('NFC').trim();
    return theme.themeId === 'defaultId' || normalized === '테마 없음';
};

/**
 * Convert width/height pairs into a friendly aspect-ratio label.
 */
export const getAspectRatioType = (width: number, height: number): string => {
    const ratio = width / height;
    const tolerance = 0.05; // 5% tolerance

    const commonRatios: Record<string, number> = {
        '1:1': 1,
        '4:3': 4 / 3,
        '3:4': 3 / 4,
        '16:9': 16 / 9,
        '9:16': 9 / 16,
        '3:2': 3 / 2,
        '2:3': 2 / 3,
    };

    for (const [name, value] of Object.entries(commonRatios)) {
        if (Math.abs(ratio - value) < tolerance) {
            return name;
        }
    }

    return `${width}:${height}`;
};
